"use client";

import { useState, useEffect } from "react";
import {
  Link,
  useLocation,
  useNavigate,
  useSearchParams,
} from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowLeftLong,
  faArrowRightLong,
  faSync,
} from "@fortawesome/free-solid-svg-icons";
import { useQuery } from "@tanstack/react-query";

import CaseManager from "../../../styles/case-manager-modules/caseManagerDashboard.module.css";
import styles from "../../../styles/patient-page-modules/PatientDetail.module.css";
import Charts from "../../../components/case-manager/reading-reviews/Charts";
import Heading from "../../../components/shared-modules/Heading";
import Readings from "../../../components/patient-detail/readings";
import MonitoringCard from "../../../components/shared-modules/monitoring-time-view/monitoring";
import Spinner from "../../../components/spinner";
import StatusModal from "../../../components/case-manager/modal/StatusModal";
import Notes from "../../../components/patient-detail/notes";
import Button from "../../../components/button-v1";
import { pdf } from "@react-pdf/renderer";
import { saveAs } from "file-saver";
// import OORSummaryPreview from "../../../components/case-manager/reading-reviews/OORSummaryPreview";
import logo from "../../../assets/images/toca_health_pdf_logo.png";

import { getPatientGeneralDetails } from "../../../services/patient/details";
import {
  getOutOfRangeReadings,
  getPatientOutOfRangeStats,
} from "../../../services/admin-case-manager/readings";
import DailiesOORTracker from "../../../components/shared-modules/time-tracker/DailiesOORTracker";
import toast from "react-hot-toast";
import useRoles from "../../../hooks/useRoles";


import { Import } from "lucide-react";

const EscalatedReview = () => {
  const { roles } = useRoles();
  const [_, setQueryParams] = useSearchParams();

  const [readingType, setReadingType] = useState("blood_pressure");
  const [sectionOpen, setSectionOpen] = useState(true);
  const [activeTab, setActiveTab] = useState("Reading Review");
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isSectionOpen, setIsSectionOpen] = useState({
    section_3_right: true,
  });

  const [currentWorkflow, setCurrentWorkflow] = useState(() => {
    return sessionStorage.getItem("caseManagerFilterState") || "All Patients";
  });
  const [modalOpen, setModalOpen] = useState(false);
  const [modalProps, setModalProps] = useState({
    title: "",
    message: "",
    status: "",
    isLoading: false,
  });

  const location = useLocation();
  const navigate = useNavigate();

  const [triggerSave, setTriggerSave] = useState(false);

  useEffect(() => {
    if (triggerSave) {
      setTimeout(() => setTriggerSave(false), 100);
    }
  }, [triggerSave]);

  const searchParams = new URLSearchParams(location.search);
  const patientId =
    searchParams.get("patient_id") || sessionStorage.getItem("_pt_id");
  const locationId = sessionStorage.getItem("location_id") || "1";

  const patientList = (() => {
    try {
      const list = JSON.parse(sessionStorage.getItem("_ap") || "[]");
      return Array.isArray(list) ? list : [];
    } catch (error) {
      console.error("Error parsing patient list:", error);
      return [];
    }
  })();

  const currentPatientIndex = patientList.indexOf(patientId);
  const isFirstPatient = currentPatientIndex <= 0;
  const isLastPatient = currentPatientIndex >= patientList.length - 1;

  useEffect(() => {
    const timer = setTimeout(() => setIsLoading(false), 500);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (patientId) {
      sessionStorage.setItem("_pt_id", patientId);
    }
  }, [patientId]);

  const {
    data: patient,
    isLoading: isPatientLoading,
    refetch: refetchPatient,
  } = useQuery(
    ["patient-details", patientId],
    () => getPatientGeneralDetails(patientId),
    {
      enabled: !!patientId,
      onSuccess: (data) => {
        // Patient data loaded successfully
      },
      onError: (error) => {
        console.error("Error fetching patient details:", error);
        setModalProps({
          title: "Error",
          message: "Failed to load patient details",
          status: "error",
          isLoading: false,
        });
        setModalOpen(true);
      },
    }
  );
  

  useEffect(() => {
    if (patient) {
      setQueryParams((previous) => {
        const params = new URLSearchParams(previous);
        // params.set("CCM", Number(patient.ccm));
        params.set("RPM", Number(patient.rpm));
        // params.set("patient_id", patientId);
        // params.delete("active_tab");
        return params;
      });
    }
  }, [patient]);

  const closeModal = () => {
    setModalOpen(false);
  };

  const showLoadingModal = (title, message) => {
    setModalProps({
      title,
      message,
      status: "",
      isLoading: true,
    });
    setModalOpen(true);
  };

  const updateModalStatus = (status, message) => {
    setModalProps((prev) => ({
      ...prev,
      status,
      message,
      isLoading: false,
    }));
  };

  const {
    data: readingsData,
    isLoading: isReadingsLoading,
    refetch: refetchReadings,
  } = useQuery({
    queryKey: ["outOfRangeReadings", patientId, readingType],
    queryFn: () => getOutOfRangeReadings(patientId),
    enabled: !!patientId,
    onSuccess: (data) => {
      const readings = data?.readings || [];
      const hasOutOfRange = readings.length > 0;

      if (hasOutOfRange && activeTab === "Reading Review") {
        setActiveTab("Out Of Range");
      } else if (!hasOutOfRange) {
        setActiveTab("Reading Review");
      }
    },
    onError: (error) => {
      console.error("Error fetching readings:", error);
      setModalProps({
        title: "Error",
        message: "Failed to load readings",
        status: "error",
        isLoading: false,
      });
      setModalOpen(true);
    },
  });

  const {
    data: patientStats,
    isLoading: isStatsLoading,
    refetch: refetchStats,
  } = useQuery({
    queryKey: ["patientOutOfRangeStats", locationId],
    queryFn: () =>
      getPatientOutOfRangeStats(locationId, location.state?.backURL),
    onError: (error) => {
      console.error("Error fetching patient statistics:", error);
    },

    cacheTime: 0,
  });
 

  const outOfRangeReadings = readingsData?.readings || [];
  const hasOutOfRangeReadings = outOfRangeReadings.length > 0;
  const patientName = patient?.user
    ? `${patient.user.first_name} ${patient.user.last_name}`
    : "Patient";
  const patientLocation = patient?.location?.name || "Location";

  const outOfRangeCount = patientStats?.outOfRangeCount || 0;
  const totalPatients = patientStats?.totalPatients || 0;

  // Get progress percentage directly from backend
  const getProgressPercentage = () => {
    if (!patient?.escalated_readings_frequency) return 0;
    return Math.round(patient.escalated_readings_frequency);
  };

  const isDataLoading =
    isLoading ||
    isPatientLoading ||
    isReadingsLoading ||
    isStatsLoading ||
    isRefreshing;

  const handleReadingTypeSelection = (value) => {
    setReadingType(value);
  };
const handleDownloadOORSummary = () => {
  const url = `/patient/oor-summary-pdf?patientId=${patientId}`;
  window.open(url, "_blank");
};


  const goBack = () => {
    setTriggerSave(true);
    const backURL = sessionStorage.getItem("_back_url");
    if (backURL) {
      setTimeout(() => {
        navigate(backURL);
      }, 300);
    }
  };

  const handlePrevPatient = () => {
    if (!isFirstPatient && currentPatientIndex > 0) {
      setTriggerSave(true);
      setTimeout(() => {
        const prevPatientId = patientList[currentPatientIndex - 1];
        sessionStorage.setItem("_pt_id", prevPatientId);
        navigate(`${window.location.pathname}?patient_id=${prevPatientId}`, {
          state: {
            backURL: location?.state?.backURL,
          },
        });
      }, 300);
    }
  };

  const handleNextPatient = () => {
    if (!isLastPatient && currentPatientIndex < patientList.length - 1) {
      setTriggerSave(true);
      setTimeout(() => {
        const nextPatientId = patientList[currentPatientIndex + 1];
        sessionStorage.setItem("_pt_id", nextPatientId);

        navigate(`${window.location.pathname}?patient_id=${nextPatientId}`, {
          state: {
            backURL: location?.state?.backURL,
          },
        });
      }, 300);
    }
  };

  const handleRefresh = () => {
    setIsRefreshing(true);
    showLoadingModal("Refreshing Data", "Loading latest patient data...");

    Promise.all([refetchPatient(), refetchReadings(), refetchStats()])
      .then(() => {
        updateModalStatus("success", "Data refreshed successfully");
      })
      .catch((error) => {
        console.error("Error refreshing data:", error);
        updateModalStatus("error", "Failed to refresh data");
      })
      .finally(() => {
        setIsRefreshing(false);
      });
  };

  const toggleSection = (section) => {
    setIsSectionOpen((prevState) => ({
      ...prevState,
      [section]: !prevState[section],
    }));
  };

  const handleStatusChange = async (status, readingIds) => {
    try {
      await Promise.all([refetchReadings(), refetchStats()]);

      const updatedPatient = await refetchPatient();

      const isStillOutOfRange = updatedPatient?.data?.out_of_range === true;

      if (!isStillOutOfRange) {
        const currentList = JSON.parse(sessionStorage.getItem("_ap") || "[]");
        const updatedList = currentList.filter((id) => id !== patientId);
        sessionStorage.setItem("_ap", JSON.stringify(updatedList));

        await getPatientOutOfRangeStats();

        if (
          updatedList.length === 0 &&
          !roles.some((_role) => _role === "Case Manager")
        ) {
          navigate("/admin-case-manager");
        } else if (updatedList?.length > 0) {
          const currentIndex = currentList.indexOf(patientId);
          let nextPatientId;

          if (currentIndex < updatedList.length) {
            nextPatientId = updatedList[currentIndex];
          } else {
            nextPatientId = updatedList[updatedList.length - 1];
          }

          sessionStorage.setItem("_pt_id", nextPatientId);
          navigate(`${window.location.pathname}?patient_id=${nextPatientId}`);
        }
      }
    } catch (error) {
      console.error(`Error refreshing data after ${status} update:`, error);
    }
  };

  return (
    <div className={styles.patient_page}>
      <div
      className={styles.name_container_spacer} 
      style={{ height: "55px" }}
      >
      
      </div>

      <div className={styles.name_container}>
        <div className={styles.name_wrapper}>
          <div
            onClick={goBack}
            className={styles.back_btn}
            role="button"
            aria-label="Back"
            title="Back"
            tabIndex={0}
          >
            <FontAwesomeIcon icon={faArrowLeftLong} />
          </div>
          <div className={styles.name_and_status}>
            <h4>
              <strong>{patientName}</strong>{" "}
              <span className={styles.workflow_tag}>
                {currentWorkflow === "outOfRange"
                  ? "Out of Range"
                  : "All Patients"}{" "}
                Workflow{" "}
              </span>
            </h4>
          </div>

          <div>
            {currentWorkflow === "outOfRange" && (
              <DailiesOORTracker
                key={patientList[currentPatientIndex]}
                triggerSaveSignal={triggerSave}
                patientId={patientList[currentPatientIndex]}
                flow={"Out of Range"}
              />
            )}
          </div>

          {/* <button
            onClick={handleRefresh}
            disabled={isDataLoading}
            style={{
              background: "none",
              border: "none",
              cursor: isDataLoading ? "not-allowed" : "pointer",
              color: "#00134c",
              display: "flex",
              alignItems: "center",
              gap: "5px",
              fontSize: "14px",
              opacity: isDataLoading ? 0.7 : 1,
            }}
          >
            <FontAwesomeIcon icon={faSync} spin={isRefreshing} />
            {isRefreshing ? "Refreshing..." : "Refresh Data"}
          </button> */}
        </div>
      </div>

      <div className={CaseManager.patient_page_container}>
        <div className={styles.tabs_container}>
          <button
            className={`${styles.nav_button} ${isFirstPatient ? styles.disabled_button : ""
              }`}
            onClick={handlePrevPatient}
            disabled={isFirstPatient || isDataLoading}
            // disabled={isDataLoading}
            style={{
              opacity: isFirstPatient || isDataLoading ? 0.5 : 1,
              cursor:
                isFirstPatient || isDataLoading ? "not-allowed" : "pointer",
            }}
          >
            <FontAwesomeIcon icon={faArrowLeftLong} /> Prev Patient
          </button>

          <span className={styles.counter_tab}>
            {currentPatientIndex + 1} / {patientList.length}
          </span>

          <button
            className={`${styles.nav_button} ${isLastPatient ? styles.disabled_button : ""
              }`}
            onClick={handleNextPatient}
            disabled={isLastPatient || isDataLoading}
            // disabled={isDataLoading}
            style={{
              opacity: isLastPatient || isDataLoading ? 0.5 : 1,
              cursor:
                isLastPatient || isDataLoading ? "not-allowed" : "pointer",
            }}
          >
            Next Patient <FontAwesomeIcon icon={faArrowRightLong} />
          </button>
        </div>

        {isDataLoading ? (
          <div
            className={CaseManager.loading_container}
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              padding: "40px 0",
            }}
          >
            <Spinner />
          </div>
        ) : (
          <>
            <div className={CaseManager.sectio_2}>
              <div style={{ display: "flex", marginBottom: "0px" }}>
                {hasOutOfRangeReadings && (
                  <button
                    onClick={() => setActiveTab("Out Of Range")}
                    style={{
                      padding: "10px 20px",
                      borderRadius: "6px 6px 0 0",
                      backgroundColor:
                        activeTab === "Out Of Range" ? "#fff" : "#e5efff",
                      fontWeight: "bold",
                      color: "#00134c",
                      borderBottom: "none",
                    }}
                  >
                    Out Of Range
                  </button>
                )}
                <button
                  onClick={() => setActiveTab("Reading Review")}
                  style={{
                    padding: "10px 20px",
                    borderRadius: "6px 6px 0 0",
                    backgroundColor:
                      activeTab === "Reading Review" ? "#fff" : "#e5efff",
                    fontWeight: "bold",
                    color: "#00134c",
                    borderBottom: "none",
                  }}
                >
                  Reading Review
                </button>
              </div>

              <div>
                {activeTab === "Out Of Range" && hasOutOfRangeReadings ? (
                  <Heading
                    title="Out Of Range Readings"
                    bodyHeight="auto"
                    actions={[
                     <Button onClick={handleDownloadOORSummary}>
                      Download OOR Summary
                     </Button>

                    ]}

                    content={
                      <div>
                        <div className={CaseManager.bp_header}>
                          <div className={CaseManager.progressSection}>
                            <p className={CaseManager.progressText}>
                              <strong>{getProgressPercentage()}%</strong> Escalated Readings Frequency
                            </p>
                            <div className={CaseManager.progressBar}>
                              <div
                                className={CaseManager.progressFill}
                                style={{
                                  width: `${getProgressPercentage()}%`,
                                }}
                              ></div>
                            </div>
                            
                            <div style={{ marginTop: "10px" }}>
                              <p className={CaseManager.bp_title}>Readings:</p>
                              { }
                            </div>
                          </div>
                        </div>
                        <Charts
                          showActionButtons
                          readings={outOfRangeReadings}
                          patientBounds={patient?.data_bounds}
                          onStatusChange={handleStatusChange}
                          onRefresh={() => {
                            refetchReadings()
                            refetchStats()
                          }}
                        />


                      </div>
                    }
                  />
                ) : (
                  <Readings
                    isSectionOpen={sectionOpen}
                    onSectionClose={() => setSectionOpen(!sectionOpen)}
                    caseManager={true}
                    expandButton={false}
                  />
                )}
              </div>
            </div>
            <div className={CaseManager.section_2}>
              <div className={CaseManager.section_3_right}>
                <Notes
                  key={patientId}
                  isOpen={isSectionOpen.section_3_right}
                  onClose={() => toggleSection("section_3_right")}
                  height="470px"
                />
              </div>
              <div className={CaseManager.section_3_left}>
                <MonitoringCard noteType="rpm_note" caseManager={true} height="540px" />
              </div>
            </div>
          </>
        )}
      </div>

      { }
      <StatusModal
        isOpen={modalOpen}
        onClose={closeModal}
        title={modalProps.title}
        message={modalProps.message}
        status={modalProps.status}
        isLoading={modalProps.isLoading}
      />
    </div>
  );
};

export default EscalatedReview;
